import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { TagsService } from "../services/tags.services";
import { CommonService } from "src/app/shared/common.service";
import * as tagsActions from "./tags.actions"
import { catchError, finalize, map, of, switchMap, tap } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { ITag } from "../models/tags.models";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_PAGE_SIZE } from "src/app/config/global.config";

@Injectable()
export class TagsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private tagsServices: TagsService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) { }

  // fetch All Tags
  FetchAllTagsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tagsActions.FetchAllTags),
      tap(() => {
        this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: true }));
      }),
      switchMap(({ includes }) =>
        this.tagsServices.listAllTags(includes).pipe(
          map((res: APIResponse<ITag>) => tagsActions.FetchAllTagsSuccess({ tags: res.data })),
          catchError(() => of(
            tagsActions.FetchAllTagsError()
          )),
          finalize(() => {
            this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // fetch Tags
  FetchTagsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tagsActions.FetchTags),
      tap(() => {
        this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.tagsServices.listTags(action.page, action.key).pipe(
          map((res: APIResponse<ITag>) => tagsActions.FetchTagsSuccess({ tags: res })),
          catchError(() => of(
            tagsActions.FetchTagsError()
          )),
          finalize(() => {
            this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: false }));
          })
        )
      ),
    )
  );

  // add Tag
  AddTagEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tagsActions.AddTag),
      tap(() => {
        this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.tagsServices.addTag(action.tag).pipe(
          map((res: APIResponse<ITag>) => tagsActions.AddTagSuccess({ tag: res.data[0] })),
          tap(() => {
            this.store.dispatch(tagsActions.FetchTags({page:{number:1, size: DEFAULT_PAGE_SIZE}}))
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.tagAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(tagsActions.AddTagError())),
          finalize(() => {
            this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Update Tag
  UpdateTagEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tagsActions.UpdateTag),
      tap(() => {
        this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.tagsServices.updateTag(action.id, action.tag).pipe(
          map((res: APIResponse<ITag>) => tagsActions.UpdateTagSuccess({ tag: res.data[0] })),
          tap(() => {
            this.store.dispatch(tagsActions.FetchTags({page:{number:1, size: DEFAULT_PAGE_SIZE}}))
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.tagUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(tagsActions.UpdateTagError())),
          finalize(() => {
            this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Delete Tag
  DeleteTagEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tagsActions.DeleteTag),
      tap(() => {
        this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.tagsServices.deleteTag(action.id).pipe(
          map((res: APIResponse<ITag>) => tagsActions.DeleteTagSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.tagDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(tagsActions.DeleteTagError())),
          finalize(() => {
            this.store.dispatch(tagsActions.SetTagsLoader({ isLoading: false }));
          })
        )
      )
    )
  );
}
